import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useI18n } from '../i18n/LocaleContext'

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('h-4 w-4 animate-spin', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.2" />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useI18n()
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-[14px] text-gray-600">
      <Spinner />
      {label ?? t.common.loading}
    </div>
  )
}

export function ErrorBlock({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  const { t } = useI18n()
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <p className="text-[14px] text-red-700" role="alert">
        {message ?? t.common.error}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="text-[13px] font-semibold text-brand-700 underline-offset-[3px] hover:underline"
        >
          {t.common.retry}
        </button>
      )}
    </div>
  )
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode
  title: string
  body?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-strong bg-surface-muted px-6 py-14 text-center">
      {icon && (
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sage-100 text-sage-700">
          {icon}
        </div>
      )}
      <div>
        <h3 className="font-display text-[19px] font-normal text-gray-900">{title}</h3>
        {body && (
          <p className="mx-auto mt-1.5 max-w-md text-[14px] leading-relaxed text-gray-600">{body}</p>
        )}
      </div>
      {action}
    </div>
  )
}

/** Inline "saved / failed" strip used by the autosaving editors. */
export function SaveState({ state }: { state: 'idle' | 'saving' | 'saved' | 'error' }) {
  const { t } = useI18n()
  if (state === 'idle') return null
  if (state === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12.5px] text-gray-500">
        <Spinner className="h-3 w-3" />
        {t.common.saving}
      </span>
    )
  }
  if (state === 'saved') {
    return <span className="text-[12.5px] text-gray-500">{t.common.allSaved}</span>
  }
  return (
    <span className="text-[12.5px] text-red-600" role="alert">
      {t.common.saveFailed}
    </span>
  )
}
